/**
 * V2Adapter — Bridge between the V1 Cloudscape shell and V2 Stencil pages.
 *
 * This is the E1 foundation from the parity analysis doc. It provides the
 * 4 bridges needed for any V2 component to render correctly inside the
 * V1 desktop shell:
 *
 *   1. StencilProvider — injects CSS design tokens so Stencil components
 *      render with proper spacing, colors, and typography.
 *   2. QueryClientProvider — V2 hooks use React Query. The V1 shell already
 *      has one, so we reuse it (the adapter does NOT create a new one).
 *   3. DesktopAuthBridge — reshapes the V1 Redux/context user data into
 *      the V2 `useCurrentUser()` context shape.
 *   4. ErrorBoundary — isolates V2 component crashes from the V1 shell.
 *
 * Usage:
 *   <V2Adapter>
 *     <V2AuditsPage />
 *   </V2Adapter>
 *
 * Lifecycle:
 *   - Phase 1 (hybrid): V1 Shell → V2Adapter → V2 Page
 *   - Phase 2 (full v2): V2Adapter deleted, unified shell provides everything natively
 */
import React from 'react';
import { StencilProvider, ThemeMode } from '@amzn/stencil-react-components/context';
import { ErrorBoundary } from 'src/v2/shared/errors/ErrorBoundary';
import { DesktopAuthBridge } from './DesktopAuthBridge';
import { MobileI18nProvider } from 'src/v2/shared/i18n/MobileI18nProvider';

interface V2AdapterProps {
  readonly children: React.ReactNode;
}

/**
 * Wraps V2 content with the providers it needs to render inside the V1 shell.
 *
 * What it provides:
 * - StencilProvider: CSS design tokens (spacing, color, typography variables)
 * - MobileI18nProvider: arb-tools translation bundles (useMessages works)
 * - DesktopAuthBridge: V1 user context → V2 useCurrentUser() shape
 * - ErrorBoundary: crash isolation
 *
 * What it does NOT provide (already in the V1 tree above it):
 * - BrowserRouter (V1 already wraps everything in BrowserRouter)
 * - QueryClientProvider (V1 App.tsx already has one)
 * - FeatureFlagsProvider (already at index.tsx level)
 */
export function V2Adapter({ children }: V2AdapterProps): JSX.Element {
  return (
    <StencilProvider themeMode={ThemeMode.Light}>
      <ErrorBoundary>
        <MobileI18nProvider>
          <DesktopAuthBridge>{children}</DesktopAuthBridge>
        </MobileI18nProvider>
      </ErrorBoundary>
    </StencilProvider>
  );
}
